import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { Api } from 'services';
import { COLORS } from 'components/UserDashboardV2/utils/constants';

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M6 9L12 15L18 9" stroke="#666666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M20 6L9 17L4 12" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const fieldLabelStyle = {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    color: "#191919",
    marginBottom: "6px",
    display: "block",
};

const disabledInputStyle = {
    height: "46px",
    padding: "0 14px",
    borderRadius: "12px",
    border: "1px solid #E6E6E6",
    backgroundColor: "#F5F5F5",
    color: "#666666",
    fontFamily: "Roboto, sans-serif",
    fontSize: "15px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    cursor: "not-allowed",
};

const Dropdown = ({ label, required, placeholder, value, options, getLabel, getValue, popoverKey, openKey, onOpen, onSelect }) => {
    const isOpen = openKey === popoverKey;
    const selectedOpt = (options || []).find(o => String(getValue(o)) === String(value));
    const displayValue = selectedOpt ? getLabel(selectedOpt) : "";

    return (
        <div data-av2-popover style={{ position: "relative", flex: "1 1 100%", minWidth: 0 }}>
            <label style={fieldLabelStyle}>
                {label}{required && <span style={{ color: "#FF414D", marginLeft: "3px" }}>*</span>}
            </label>
            <div
                onClick={() => onOpen(isOpen ? null : popoverKey)}
                style={{
                    height: "46px",
                    padding: "0 14px",
                    borderRadius: "12px",
                    border: "1px solid #E6E6E6",
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    boxSizing: "border-box",
                }}
            >
                <span style={{ color: value ? "#191919" : "#B8B8B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "Roboto, sans-serif", fontSize: "15px" }}>
                    {value ? displayValue : placeholder}
                </span>
                <ChevronDownIcon />
            </div>
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    width: '100%',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    padding: '6px 0',
                    zIndex: 30,
                    boxSizing: 'border-box',
                }}>
                    {options.length === 0 ? (
                        <div style={{ padding: "10px 14px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#8A8A8A" }}>
                            No other batches available
                        </div>
                    ) : options.map((opt, i) => {
                        const optVal = getValue(opt);
                        const optLabel = getLabel(opt);
                        const isSelected = String(optVal) === String(value);
                        return (
                            <div
                                key={optVal || i}
                                onClick={() => onSelect(optVal)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 16px',
                                    fontFamily: 'Roboto, sans-serif',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    color: isSelected ? COLORS.primary : "#191919",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                            >
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{optLabel}</span>
                                {isSelected && <CheckIcon />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ChangeBatchModal = ({ visible, onClose, onAssign, courseName, courseId, currentStandardId, excludeStandardIds, title }) => {
    const [selectedBatch, setSelectedBatch] = useState('');
    const [batchOptions, setBatchOptions] = useState([]);
    const [openPopover, setOpenPopover] = useState(null);
    const [assigning, setAssigning] = useState(false);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest("[data-av2-popover]")) {
                setOpenPopover(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!visible || !courseId) {
            setBatchOptions([]);
            setSelectedBatch('');
            setOpenPopover(null);
            setAssigning(false);
            return;
        }
        Api.get("/standards").params({ courseId }).send((res) => {
            let batches = [];
            if (Array.isArray(res)) {
                batches = res;
            } else if (res?.data) {
                if (Array.isArray(res.data)) {
                    batches = res.data;
                } else if (typeof res.data === 'object') {
                    batches = Object.values(res.data).flat();
                }
            }
            if (Array.isArray(excludeStandardIds)) {
                // Assign new batch: show only batches the student isn't already in.
                const excluded = excludeStandardIds.map(String);
                setBatchOptions(batches.filter(b => !excluded.includes(String(b.id || b.standardId))));
            } else if (currentStandardId) {
                const otherBatches = batches.filter(b => String(b.id || b.standardId) !== String(currentStandardId));
                setBatchOptions(otherBatches.length > 0 ? otherBatches : batches);
            } else {
                setBatchOptions(batches);
            }
        });
    }, [visible, courseId, currentStandardId, excludeStandardIds]);

    const handleClose = () => {
        if (assigning) return;
        setSelectedBatch('');
        setOpenPopover(null);
        onClose();
    };

    const handleAssign = () => {
        if (!selectedBatch) return;
        setAssigning(true);
        onAssign({ standardId: selectedBatch }, (success) => {
            setAssigning(false);
            if (success) {
                setSelectedBatch('');
                onClose();
            }
        });
    };

    const formatBatchLabel = (b) => {
        if (!b) return "";
        if (b.std && b.section) {
            return `${b.std} - ${b.section}`;
        }
        const stdVal = b.std || b.batchName || b.standardName || b.name || "";
        const secVal = b.section || b.sectionName || "";
        if (stdVal && secVal) {
            return `${stdVal} - ${secVal}`;
        }
        if (b.name) return b.name;
        if (b.standardName) return b.standardName;
        return stdVal || secVal || `Batch ${b.id || ''}`;
    };

    return (
        <Modal
            visible={visible}
            title={null}
            footer={null}
            onCancel={handleClose}
            maskClosable={!assigning}
            centered
            width={520}
            className="modal-round-corner"
            bodyStyle={{ padding: "32px" }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 600, fontSize: "22px", color: "#191919", textAlign: "center", display: "block", width: "100%" }}>
                    {title || "Change batch"}
                </span>

                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                    {/* Course Field - Disabled */}
                    <div>
                        <label style={fieldLabelStyle}>
                            Course<span style={{ color: "#FF414D", marginLeft: "3px" }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={courseName || ""}
                            disabled
                            style={disabledInputStyle}
                        />
                    </div>

                    {/* Select Batch Dropdown */}
                    <Dropdown
                        label="Select batch"
                        required
                        placeholder="Select batch"
                        value={selectedBatch}
                        options={batchOptions}
                        getLabel={formatBatchLabel}
                        getValue={o => o?.id || o?.standardId}
                        popoverKey="change-batch-dropdown"
                        openKey={openPopover}
                        onOpen={setOpenPopover}
                        onSelect={(val) => {
                            setSelectedBatch(val);
                            setOpenPopover(null);
                        }}
                    />
                </div>

                {/* Footer Buttons */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginTop: "12px" }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={assigning}
                        style={{
                            height: "44px",
                            padding: "0 24px",
                            borderRadius: "12px",
                            border: "1px solid #E6E6E6",
                            backgroundColor: "#FFFFFF",
                            color: "#636363",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "15px",
                            fontWeight: 600,
                            cursor: assigning ? "not-allowed" : "pointer",
                        }}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        onClick={handleAssign}
                        disabled={!selectedBatch || assigning}
                        style={{
                            height: "44px",
                            padding: "0 28px",
                            borderRadius: "12px",
                            border: "none",
                            backgroundColor: (!selectedBatch && !assigning) ? "#CCCCCC" : COLORS.primary,
                            color: "#FFFFFF",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "15px",
                            fontWeight: 700,
                            cursor: (!selectedBatch || assigning) ? "not-allowed" : "pointer",
                            // Same in-progress look as the confirmation modals (SavingModal).
                            opacity: assigning ? 0.7 : 1,
                        }}
                    >
                        {assigning ? "ASSIGNING..." : "ASSIGN"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ChangeBatchModal;
